/*@jsxRuntime automatic @jsxImportSource react*/
/*<!-- vale Google.Headings = NO -->*/
/*<!-- vale Google.Headings = YES -->*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1737072000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Fri Jan 17 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "intercom",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Intercom",
          "depth": 0,
          "id": "connect-to-intercom"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing contacts",
          "depth": 1,
          "id": "syncing-contacts"
        }, {
          "value": "Syncing companies",
          "depth": 1,
          "id": "syncing-companies"
        }, {
          "value": "Syncing events",
          "depth": 1,
          "id": "syncing-events"
        }, {
          "value": "Tags",
          "depth": 0,
          "id": "tags"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      a: "a",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      img: "img",
      h3: "h3",
      h4: "h4",
      ul: "ul",
      li: "li",
      code: "code",
      h5: "h5",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {EventSyncWarning, CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch can create and update both contacts and company objects within the ", _jsx(_components.a, {
          href: "https://developers.intercom.com/intercom-api-reference/reference#contacts-model",
          children: "Intercom Contacts Model"
        }), " and ", _jsx(_components.a, {
          href: "https://developers.intercom.com/intercom-api-reference/reference#company-model",
          children: "Intercom Company Model"
        }), " respectively."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Contact objects"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Intercom contacts."
            }), _jsx(_components.td, {
              children: "Upsert, Update, Archive"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.intercom.com/docs/references/rest-api/api.intercom.io/Contacts/CreateContact",
                children: "Contact docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Company objects"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Intercom company objects."
            }), _jsx(_components.td, {
              children: "Upsert, Update"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.intercom.com/intercom-api-reference/reference/createorupdatecompany",
                children: "Company docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Events"
              })
            }), _jsx(_components.td, {
              children: "Sync records as events to Intercom. This is often in the form of a track call."
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.intercom.com/intercom-api-reference/reference/createdataevent",
                children: "Event docs"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-intercom",
        children: "Connect to Intercom"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Intercom"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to Intercom with OAuth. Click ", _jsx(_components.strong, {
          children: "Log in to Intercom"
        }), " and log into your Intercom account."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-intercom-config-auth.png",
          alt: "Creating an association in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "Once successful, you will be redirected back to Hightouch to enter a descriptive name for your destination and complete setup."
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Intercom destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Intercom destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-contacts",
        children: "Syncing contacts"
      }), "\n", _jsx(_components.p, {
        children: "Contact objects allow you to represent and provide information on individual users. All contact objects are represented as users or leads as marked by the role attribute."
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["When looking at the ", _jsx(_components.a, {
            href: "https://www.intercom.com/help/en/articles/318-the-people-company-and-account-list-explained#the-people-list",
            children: "People list"
          }), " or when ", _jsx(_components.a, {
            href: "https://www.intercom.com/help/en/articles/2848161-create-more-targeted-audiences-with-and-or-rules",
            children: "creating an audience"
          }), ", the number of contacts (users) displayed might differ from the number of rows processed by your sync. This is because Intercom ", _jsx(_components.a, {
            href: "https://www.intercom.com/help/en/articles/5537581-include-unsubscribed-contacts-in-your-email-audience#h_c8fcd619b4",
            children: "automatically filters out unsubscribed contacts"
          }), " from your contact list."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "To match rows from your model to objects in Intercom, you need to select a model column and corresponding Intercom field. You can match on any of the following Intercom fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Email"
        }), "\n", _jsx(_components.li, {
          children: "External User ID"
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can map data from any of your model columns to contact fields in Intercom. Ensure the data type of your model column matches the data type of the field you want to sync to."
      }), "\n", _jsx(_components.h4, {
        id: "associations",
        children: "Associations"
      }), "\n", _jsx(_components.p, {
        children: "You can associate companies to contacts using this configuration section. To do so, select a column from your model to match the associated object. Hightouch uses this column to look up the associated object's ID and make the connection between the two objects."
      }), "\n", _jsxs(_components.p, {
        children: ["For example, if you wanted to associate a company to your contact, you select ", _jsx(_components.strong, {
          children: "Company"
        }), " from the right dropdown. Then, select a field to match the company on, for example Company ID."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-intercom-association.png",
          alt: "Creating an association in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/syncs/overview#delete-behavior",
          children: "delete behavior"
        }), " you select dictates what to do when a row no longer appears in your model's query results. You have the following options:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the contact in Intercom"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Clear fields"
              })
            }), _jsx(_components.td, {
              children: "Keep the contact in Intercom but clear the synced fields"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Remove from list"
              })
            }), _jsx(_components.td, {
              children: "Remove the contact from Intercom"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-companies",
        children: "Syncing companies"
      }), "\n", _jsx(_components.p, {
        children: "Company objects allow you to represent and provide information on organizations using your product. Each company has its own description and can be associated with contacts."
      }), "\n", _jsx(_components.p, {
        children: "Company objects' Company ID can't be updated. If you need to change a company object's Company ID you need to delete it and recreate it with a new Company ID."
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["When syncing company objects, make sure they have at least one ", _jsx(_components.a, {
            href: "#associations-1",
            children: "associated contact"
          }), ". Otherwise, the company won't be visible in Intercom."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["To match rows from your model to records in Intercom, you need to select the model column that contains values that match the ", _jsx(_components.strong, {
          children: "Company ID"
        }), " field. If there isn't a company with the matching Company ID, Hightouch creates a new company."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can map data from any of your model columns to company fields in Intercom. Ensure the data type of your model column matches the data type of the field you want to sync to."
      }), "\n", _jsx(_components.h4, {
        id: "associations-1",
        children: "Associations"
      }), "\n", _jsx(_components.p, {
        children: "You can associate contacts to companies using this configuration section. To do so, select a column from your model to match to the associated object. Hightouch uses this column to look up the associated object's ID and make the connection between the two objects."
      }), "\n", _jsxs(_components.p, {
        children: ["For example, if you wanted to associate a company to your contact, you select ", _jsx(_components.strong, {
          children: "Contact"
        }), " from the right dropdown. Then, select a field to match the contact on, for example ", _jsx(_components.code, {
          children: "user_id"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-intercom-association-2.png",
          alt: "Creating an association in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior-1",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/syncs/overview#delete-behavior",
          children: "delete behavior"
        }), " you select dictates what to do when a row no longer appears in your model's query results. You have the following options:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the company in Intercom"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Clear fields"
              })
            }), _jsx(_components.td, {
              children: "Keep the company in Intercom but clear the synced fields"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Remove from list"
              })
            }), _jsx(_components.td, {
              children: "Remove the company from Intercom"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-events",
        children: "Syncing events"
      }), "\n", _jsxs(_components.p, {
        children: ["When syncing events, Hightouch treats any records added to ", _jsx(_components.a, {
          href: "/getting-started/concepts",
          children: "your source"
        }), " as new events and sends them to Intercom when your sync runs."]
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h4, {
        id: "event-name",
        children: "Event name"
      }), "\n", _jsxs(_components.p, {
        children: ["Providing an event name is required to log an event in Intercom. You can either provide a static value or select to ", _jsx(_components.strong, {
          children: "use a column"
        }), " from your model."]
      }), "\n", _jsxs(_components.p, {
        children: ["Refer to ", _jsx(_components.a, {
          href: "https://developers.intercom.com/intercom-api-reference/reference/createdataevent",
          children: "Intercom's event docs"
        }), " for guidance on event naming."]
      }), "\n", _jsx(_components.h4, {
        id: "event-timestamp",
        children: "Event timestamp"
      }), "\n", _jsx(_components.p, {
        children: "You can optionally select a column that contains timestamps of when events occurred. If this field is empty, Hightouch uses the time the event arrives at the server."
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-2",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can select model columns to sync to native and custom event parameters in the field mapping section."
      }), "\n", _jsx(_components.h2, {
        id: "tags",
        children: "Tags"
      }), "\n", _jsx(_components.p, {
        children: "You can manage tags on companies and contacts in Intercom using Hightouch."
      }), "\n", _jsx(_components.p, {
        children: "The data in your source should contain the name of one or more tags for each row."
      }), "\n", _jsx(_components.p, {
        children: "For multiple tags, your source can contain an array or a comma-separated string of tag names."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-intercom-tags.png",
          alt: "Mapping to the tags field in Intercom in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h4, {
        id: "tag-modes",
        children: "Tag Modes"
      }), "\n", _jsx(_components.p, {
        children: "Syncs can have different tag modes based on how you would like to handle tags."
      }), "\n", _jsx(_components.h5, {
        id: "create-added-tags",
        children: _jsx(_components.strong, {
          children: "Create added tags"
        })
      }), "\n", _jsx(_components.p, {
        children: "When there are tags added to the source, create the tags in Intercom. If the tag does not already exist, it will be created in Intercom. If the tag already exists, it will be added to the contact or company. Do nothing for tags removed from the source."
      }), "\n", _jsx(_components.h5, {
        id: "overwrite-existing-tags",
        children: _jsx(_components.strong, {
          children: "Overwrite existing tags"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["When there are changes to tags in the source, ", _jsx(_components.strong, {
          children: "remove all tags"
        }), " in Intercom before creating the tags present in the source. If there are no changes to the tags, do nothing. Because this removes all tags in Intercom, this may delete data not authored by Hightouch."]
      }), "\n", _jsx(_components.h5, {
        id: "delete-removed-tags-and-create-added-tags",
        children: _jsx(_components.strong, {
          children: "Delete removed tags and create added tags"
        })
      }), "\n", _jsx(_components.p, {
        children: "When there are changes to tags in the source, removes tags in Intercom that have been removed from the source, and creates tags that have been added to the source."
      }), "\n", _jsxs(_components.p, {
        children: ["Read more about how Intercom handles tags ", _jsx(_components.a, {
          href: "https://developers.intercom.com/docs/references/rest-api/api.intercom.io/tags",
          children: "here"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", "\n", _jsx(_components.h4, {
        id: "429-rate-limit-exceeded",
        children: "429 Rate limit exceeded"
      }), "\n", _jsx(_components.p, {
        children: "The full error message looks something like this:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["Custom data attributes could not be created: StatusCodeError: ", _jsx(_components.span, {
            className: "hljs-number",
            children: "429"
          }), " -\n{\n  \"type\": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"error.list\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"request_id\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"003drrt76xkssahyhgd41\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"errors\""
          }), ":[{\n    \"", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "code"
          }), "\": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"rate_limit_exceeded\""
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"message\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Exceeded rate limit of 167 in 10_seconds\""
          }), "\n  }]\n}\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["This error means you've exceeded ", _jsx(_components.a, {
          href: "https://developers.intercom.com/intercom-api-reference/reference/rate-limiting",
          children: "Intercom's rate limit"
        }), ".\nOne reason for this could be running two Hightouch syncs to Intercom simultaneously.\nTry staggering the syncs so that they don't overlap."]
      }), "\n", "\n", _jsx(_components.h4, {
        id: "missing-companies",
        children: "Missing companies"
      }), "\n", _jsxs(_components.p, {
        children: ["If your Hightouch sync processes successfully, but you don't see the companies\nin Intercom, check if there is at least one contact ", _jsx(_components.a, {
          href: "#associations-1",
          children: "associated to the company"
        }), ". Intercom only displays companies with at least one associated contact. For more information, read the Intercom ", _jsx(_components.a, {
          href: "https://developers.intercom.com/intercom-api-reference/reference/create-or-update-a-company",
          children: "company API documentation"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
